<template>
    <main class="aup-public-profile version2024-06-25b">
        <section
            class="d-flex justify-content-center align-items-center"
            style="min-height: 75vh;"
            v-if="isLoading"
        >
        <BaseBoxLoader />
        </section>
        <section v-if="!isLoading && profileErrorStatus" class="py-xl-12 py-md-8 py-5 bg-dark aup-container">
            <div class="row justify-content-center">
                <div class="col-xl-6 col-lg-7">
                    <h1 class="text-white text-lg-center mb-4">Sorry! It seems that </h1>
                </div>
            </div>
            <div class="row  justify-content-center">
                <div class="col-lg-7 col-xl-6">
                    <p class="text-white" v-if="profileErrorStatus === 'This profile has not been created'">The profile url <span class="text-primary">error:call:6</span> has not yet been claimed. Create a public profile now to start sharing your work on <IconAlpha style="fill: currentColor; height: .5625rem; margin: 0.0625rem 0.125rem 0 0;"/> Universe</p>
                </div>
            </div>
        </section>
        <div v-if="!isLoading && !profileErrorStatus">
            <!-- HERO SECTION -->
            <section class="justify-content-center">
                <!--HEADER BACKGROUND IMAGE-->
                <div
                    class="aup-public-profile__header-background"
                    :style="calcBackdropPhoto"
                    role="img"
                    :aria-label="`Cover photo of ${profileData.profile.ppd_displayname}`"
                >
                    <!--EXPAND ICON-->
                <div
                    v-if="profileData.contact.ppd_ispublic"
                    class="aup-public-profile__expand-icon-container"
                >
                    <IconExpand
                        role="button"
                        :height="30"
                        :width="30"
                        @click.native="expandHeroBackground"
                        aria-label="Expand background"
                    />
                </div>
                <div
                    v-if="isSelfProfile"
                    class="aup-public-profile__edit-profile-btn"
                >
                    <a :href="`${baseEditURL}publicprofile`">
                        <BaseButton
                        class="mt-2"
                        >Edit Profile</BaseButton>
                    </a>
                </div>

                <!-- HERO CONTENT -->
                <div class="aup-container aup-public-profile__hero-wrapper">
                    <Hero
                        :loggedIn="loggedIn"
                        :heroBoxIsEmpty="heroBoxIsEmpty()"
                        :isBlankProfile="!galleryLoaded && !profileData.gearlocker.length"
                        :isSelfProfile="isSelfProfile"
                        :profileGUID="profileData.profile.ppd_publicprofileid"
                        :avatar="profilePhotoObject"
                        :name="profileData.profile.ppd_displayname"
                        :state="profileData.profile.address3_stateorprovince"
                        :country="profileData.profile.address3_country"
                        :badges="profileData.badges"
                        :badgesStatical="profileData.badgesStatical"
                        :about="profileData.contact.ppd_about"
                        :specialties="profileSpecialties"
                        :type="profileTypes"
                        :instagramLink="profileData.contact.ppd_instagramurl"
                        :twitterLink="profileData.contact.ppd_twitterurl"
                        :facebookLink="profileData.contact.ppd_facebookurl"
                        :youtubeLink="profileData.contact.ppd_youtubeurl"
                        :tiktokLink="profileData.contact.ppd_ticktokurl"
                        :threadsLink="profileData.contact.ppd_metathreadsurl"
                        :portfolioLink="profileData.contact.websiteurl"
                        :socialLinksPublic="profileData.profile.ppd_sociallinksispublic"
                        @show-report-modal="toggleReportModal"
                    />
                </div>
            </div>
            <!--END HERO SECTION-->
            </section>
            <!-- USER GALLERY-->
            <!--ct -- background image needs to be tied to feature image + blur -->
            <section
                class="position-relative aup-public-profile__gallery"
                v-if="galleryLoaded"
            >
                <!-- <BaseFilteredBackground :backgroundImage="backdropPhoto" /> -->

                <div class="aup-container">
                    <!-- MASONRY GALLERY START-->
                    <Gallery
						:isSelfProfile="isSelfProfile"
                        :userName="profileData.profile.ppd_displayname"
                        :heroBoxIsEmpty="heroBoxIsEmpty()"
						:equipment="equipment"
                    />
                    <!-- MASONRY GALLERY END-->
                </div>
            </section>
            <!--END GALLERY-->

            <!--GEAR START-->
            <section
                v-if="profileData.gearlocker.length"
                class="aup-public-profile__gear-container" >
                    <div class="aup-container py-5">
                    <div class="aup-lined-text d-flex align-items-center mb-4">
                        <h2 class="t-heavy mr-lg-3 aup-lined-text__heading">My Sony System</h2>
                        <div class="aup-lined-text__line aup-lined-text__line--dark d-none d-lg-block"></div>
                    </div>

                    <!--CAMERA START-->
                    <h6 class="py-4 t-micro text-uppercase" v-if="knownCameraBodies.length">camera bodies</h6>
                    <Cameras :gearCameras="knownCameraBodies" :utmCodesUsage="'profile_gear_locker'" v-if="knownCameraBodies.length" />
                    <!--CAMERA END-->

                    <!--LENS START-->
                    <h6 class="py-4 t-micro text-uppercase" v-if="knownLenses.length">lenses</h6>
                    <Lenses :gearLenses="knownLenses" :utmCodesUsage="'profile_gear_locker'" />
                    <!--LENS END-->
                </div>
            </section>
            <!--GEAR END-->

            <!--USER FOOTER START-->
            <section class="aup-public-profile__user-footer-container w-100" v-if="galleryLoaded || profileData.gearlocker.length">
                <div class="aup-public-profile__user-footer-inner">
                    <LikeProfile
                        :avatar="profilePhotoObject"
                        :backgroundImage="galleryItems.length || profileData.gearlocker.length ? backdropPhotoObject : '' "
                        :isSelfProfile="isSelfProfile"
                        :profileGUID="profileData.profile.ppd_publicprofileid"
                        :name="profileData.profile.ppd_displayname"
						@show-report-modal="toggleReportModal"
                        :loggedIn="loggedIn"
                    />
                </div>
            </section>
            <!--USER FOOTER END-->
            <!--MORE MEMBERS START-->
            <section class="aup-public-profile__more-members-container" :style="[!galleryLoaded && !profileData.gearlocker.length ? { 'padding-top': '5rem'} : '']">
                <div class="aup-public-profile__more-members-inner py-5" v-if="membersLoaded">
                    <MoreMembers :memberProfiles="moreMembers" />
                </div>
            </section>
            <!--MORE MEMBERS END-->
            <!-- MODAL POP UP TO SHOW EXPANDED VIEW FOR HEADER EXPAND -->
            <BaseModalPhoto
                v-model="showModal"
                :modalBgGuid="backdropPhoto"
                :modalBgUrl="backdropPhotoObject && backdropPhotoObject.ppd_imageurl"
                v-on:close="showModal = false"
            >
                <template slot="content">
                    <BasePhotoDetails
                        :alt="backdropPhotoObject.alt"
                        :photoTitle="backdropPhotoObject.ppd_displaytitle"
						:photoDescription="backdropPhotoObject.ppd_displaydescription"
                        :name="profileData.profile.ppd_displayname"
                        :dateTaken="backdropPhotoObject.ppd_datetaken"
                        :location="backdropPhotoObject.ppd_locationtaken"
                        v-on:close="showModal = false"
                        :cameraMake="backdropPhotoObject.ppd_cameramake"
                        :cameraModel="backdropPhotoObject.ppd_cameramodel"
                        :lensModel="backdropPhotoObject.ppd_lensmodel"
                        :staffPick="backdropPhotoObject.ppd_isstaffpick"
                        :focalLength="backdropPhotoObject.ppd_focallength"
                        :fStop="backdropPhotoObject.ppd_fnumber"
                        :exposure="backdropPhotoObject.ppd_exposuretime"
                        :ISO="backdropPhotoObject.ppd_iso"
                        :imageObject="backdropPhotoObject"
                        :categoryTags="backdropPhotoObject.ppd_categorytags"
                        :photoLikes="backdropPhotoObject.ppd_photolikes"
                        :photoShares="backdropPhotoObject.ppd_communityreactiontwocount"
                        :verifiedSonyPhoto="backdropPhotoObject.ppd_sonyverified && String(backdropPhotoObject.ppd_cameramake).toLowerCase() === 'sony'"
                        :reportItemGuid="backdropPhotoObject.ppd_usermediaid"
						:isSelfProfile="isSelfProfile"
						:imageZestyGuid="backdropPhotoObject.ppd_zestyguid"
						:imageURL="backdropPhotoObject.ppd_imageurl"
						:equipment="equipment"
                    />
                </template>
            </BaseModalPhoto>

			<!-- REPORT MODAL  -->
			<BaseReportModal
				:showReportModal="showReportModal"
				:reportReasonsList="reportReasons"
				v-on:close="showReportModal = false"
				:userName="profileData.profile.ppd_displayname"
				@onClose="showReportModal = false"
				:reportTypePhoto="false"
				:reportItemGuid="$store.state.Public.publicUser.profile.ppd_publicprofileid"
			/>

        </div>
    </main>
</template>

<script>
import Hero from '@/components/public-profile/Hero';
import Gallery from '@/components/public-profile/Gallery';
import Cameras from '@/components/public-profile/Cameras';
import Lenses from '@/components/public-profile/Lens';
import LikeProfile from '@/components/public-profile/LikeProfile';
import MoreMembers from '@/components/public-profile/MoreMembers';
import { mapState } from 'vuex';
import { isEmptyMixin, calcRespImageURLMixin } from '@/mixins/mixins.js';
import DYNAMICS_ENTITIES from "@/PROFILE_CONSTANTS/DYNAMICS_ENTITIES";
import { PROFILE_CONSTANTS } from "@/PROFILE_CONSTANTS";
import { API_GET_EQUIPMENT } from '@/api-service.js';
export default {
    mixins: [isEmptyMixin, calcRespImageURLMixin],
    props: ['username'],
    components: {
        Hero,
        Gallery,
        Cameras,
        Lenses,
        LikeProfile,
        MoreMembers,
    },
    data() {
        return {
            showModal: false,
			selectedPhoto: {},
			loggedIn: false,
            placeholderImageBackground: 'https://alphauniverseglobal.media.zestyio.com/Profile_DefaultCoverV2.jpg',
			placeholderProfilePic: 'https://alphauniverseglobal.media.zestyio.com/ProfileDisplayPicture_HumanEgg.jpg',
            membersLoaded: false,
			membersCount: 4,
            showReportModal: false,
			reportReasons: DYNAMICS_ENTITIES.REPORTING_REASONS,
			baseEditURL: PROFILE_CONSTANTS.EDIT_URL,
            equipment: {},
        };
    },
    created: async function() {
        const equipmentResponse = await API_GET_EQUIPMENT();
        if (equipmentResponse.status === 200) {
            this.equipment = equipmentResponse.data;
        }
    },
    mounted: function() {
		this.loadNewProfile();
		if ($cookies.isKey('au_guid') && $cookies.isKey('au_profile_guid')) {
			this.loggedIn = true;
        }
	},
    watch: {
        async $route() {
            await this.loadNewProfile();
        }
    },
    computed: {
        ...mapState('Public', {
            isLoading: state => state.isLoading,
            profileTypes: state => {
                let type =
                    state.publicUser.contact.ppd_photographertype_aumulti;
                return type ? type.split(',') : [];
            },
            profileLikes: state => {
                let likes = state.publicUser.profile.ppd_profilelikes;
                return likes ? likes : 0;
			},
			isSelfProfile: state => {
				return state.isSelfProfile;
            },
            profileErrorStatus: state => {
                return state.profileErrorStatus;
            }
        }),
        profileSpecialties: {
            get() {
                if(typeof this.$store.state.Public.publicUser.contact.ppd_photographerspecialty_aumulti === "string") {
					const set = new Set(this.$store.state.Public.publicUser.contact.ppd_photographerspecialty_aumulti.toLowerCase().split(','));
					return Array.from(set);
                } else {
                    return [];
                }
            }
        },
        profileData: {
            get() {
                return this.$store.state.Public.publicUser;
            }
        },
        backdropPhoto: {
            get() {
                const userMediaArray = this.$store.state.Public.publicUser.profileimages;
                if (userMediaArray) {
                    let backdropPhotoArray = [];
                    backdropPhotoArray = userMediaArray.filter(media => media.ppd_isbackdrop);
                    if (backdropPhotoArray.length) {
                        return backdropPhotoArray[0].ppd_zestyguid;
                    }
                }
                return this.placeholderImageBackground;
            }
        },
        backdropPhotoObject: {
            get() {
                const userMediaArray = this.$store.state.Public.publicUser.profileimages;
                if (userMediaArray) {
                    let backdropPhotoArray = [];
                    backdropPhotoArray = userMediaArray.filter(media => media.ppd_isbackdrop);
                    if (backdropPhotoArray.length) {
                        return backdropPhotoArray[0];
                    }
                }
                return false;
            }
        },
        profilePhotoObject: {
            get() {
                console.log('profilePhotoObject', JSON.stringify(this.$store.state.Public.publicUser));
                const userMediaArray = this.$store.state.Public.publicUser.profileimages;
                if (userMediaArray) {
                    const profilePhoto = userMediaArray.filter(media => media.ppd_isprofile);
                    if (profilePhoto.length) {
                        return profilePhoto[0];
                    }
                }
                return this.placeholderProfilePic;
            }
		},
		cameraDictionary() {
			let dictionary = {};
			if (this.equipment.SONY_CAMERAS) {
                this.equipment.SONY_CAMERAS.forEach(camera => dictionary[camera.model] = camera);
            }
			if (this.equipment.NIKON_CAMERAS) {
                this.equipment.NIKON_CAMERAS.forEach(camera => dictionary[camera.model] = camera);
            }
            if (this.equipment.CANON_CAMERAS) {
                this.equipment.CANON_CAMERAS.forEach(camera => dictionary[camera.model] = camera);
            }
			return dictionary;
		},
		lensDictionary() {
			let dictionary = {};
			if (this.equipment.SONY_LENSES) {
                this.equipment.SONY_LENSES.forEach(lens => dictionary[lens.model] = lens);
            }
			return dictionary;
        },
        knownCameraBodies: {
            get() {
                const gearLockerArray = this.profileData.gearlocker;
                if (gearLockerArray) {
					// dl: filter gear by camera bodies and gear thats in the dictionary aka known cameras
					const knownCameraBodies = gearLockerArray.filter(gear => {
						return gear.ppd_equipmenttype == '927940000' && this.cameraDictionary[gear.ppd_model];
					});
                    if (knownCameraBodies.length) {
                        return knownCameraBodies.filter((value, index, self) => //remove duplicates
							index === self.findIndex((el) => (
								el.ppd_model === value.ppd_model
							)));
                    }
                }
                return [];
            }
        },
        knownLenses: {
            get() {
                const gearLockerArray = this.profileData.gearlocker;
                if (gearLockerArray) {
					// dl: filter gear by lenses and gear thats in the dictionary aka known cameras
					const lenses = gearLockerArray.filter(gear => {
						return gear.ppd_equipmenttype == '927940001' && this.lensDictionary[gear.ppd_model];
					});
                    if (lenses.length) {
                        return lenses.filter((value, index, self) => //remove duplicates
							index === self.findIndex((el) => (
								el.ppd_model === value.ppd_model
							)));
                    }
                }
                return [];
            }
        },
        galleryItems: {
            get() {
                const userMediaArray = this.$store.state.Public.galleryMedia;
                if (!userMediaArray) {
					return [];
				}
				// loop through user media and return all photos that arent profilePicture
				return userMediaArray.filter(media => media.ppd_isprofile !== true);
            }
        },
        galleryLoaded() {
            return !!(Array.isArray(this.galleryItems) && this.galleryItems.length);
        },
        moreMembers: {
            get() {
                return this.$store.state.moreMembers;
            }
        },
        calcBackdropPhoto() {
			let bgGradient = 'linear-gradient(245deg, rgba(0,0,0,0) 0%, rgba(3,0,16,0.24) 55%, rgba(3,0,16,0.85) 95%)';
			let bgGradientMobile = 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(3,0,16,0.24) 30%, rgba(3,0,16,0.85) 95%)';
			// if backdrop photo doesnt exist use default plus gradient
			if (!this.backdropPhotoObject) {
                return {
                    'background-image':
                        bgGradient + ', ' + `url(` +
                        'https://alphauniverseglobal.media.zestyio.com/Profile_DefaultCoverV2.jpg' +
                        `)`,
                    'background-position-x': '80%',
				};
			// if backdrop photo doesnt exist and window is mobile use default plus mobile gradient
			} else if (!this.backdropPhotoObject && window.innerWidth < 992) {
				return {
                    'background-image':
                        bgGradientMobile + ', ' + `url(` +
                        'https://alphauniverseglobal.media.zestyio.com/profile-default-cover-mobile.png' +
                        `)`,
				};
			// if backdrop photo does exist and window is mobile use backdropPhoto plus mobile gradient
			} else if (this.backdropPhotoObject && window.innerWidth < 992) {
				return {
					// :style="{'background-image' : 'url(' + this.calcRespImageURL(backdropPhotoObject.ppd_zestyguid, 1920) + ' )' }"
                    'background-image': bgGradientMobile + ', ' + this.respMediaBackgroundImage(this.backdropPhotoObject.ppd_imageurl, this.backdropPhotoObject.ppd_zestyguid, 992),
                };
			// else use backdrop photo plus gradient
            } else {
                return {
                    'background-image': bgGradient + ', ' + this.respMediaBackgroundImage(this.backdropPhotoObject.ppd_imageurl, this.backdropPhotoObject.ppd_zestyguid, 1720),
                };
            }
        },
    },
    methods: {
		toggleReportModal() {
			this.showReportModal = true;
        },
        async loadNewProfile() {
            this.scrollToTop();
            await this.$store.dispatch('Public/getPublicUserData', this.username);
            if(!this.profileErrorStatus) {
                this.$store.dispatch('getMoreMembers', { profileGUID: this.profileData.profile.ppd_publicprofileid, memberCount: this.membersCount});
                this.membersLoaded = true;
                this.$store.commit('Public/checkIsSelfProfile');
            }
        },
        heroBoxIsEmpty() {
            return !(this.profileData.contact.ppd_about ||
				this.profileData.contact.ppd_instagramurl ||
				this.profileData.contact.ppd_twitterurl ||
				this.profileData.contact.ppd_facebookurl ||
                this.profileData.contact.ppd_ticktokurl ||
                this.profileData.contact.ppd_metathreadsurl ||
                this.profileData.contact.ppd_youtubeurl ||
				this.profileData.contact.websiteurl ||
				this.profileSpecialties.length > 0);
        },
        expandHeroBackground() {
            this.showModal = true;
        },
        scrollToTop() {
            window.scroll({
                top: 0,
                left: 0,
                behavior: 'smooth',
            });
        },
    },
};
</script>

<style lang="scss">
@import '../assets/scss/all';
.aup-public-profile {
    overflow-x: hidden;
    &__header-background {
        position: relative;
        z-index: 10;
        height: auto;
        width: 100%;
        background-position: 50% 50%;
        background-size: cover;
        background-repeat: no-repeat;

        @include media-breakpoint-up(md) {
            height: 42.25vw;
            min-height: 660px;
        }

        @include media-breakpoint-up(lg) {
            min-height: 585px;
        }
    }
    &__loader {
        min-height: 75vh;
    }
    &__hero-wrapper {
        display: flex;
        height: 100%;
        display: flex;
        align-items: flex-end;
    }
    .aup-container.aup-public-profile__hero-wrapper {
        @include media-breakpoint-down(md) {
            padding: 0 !important;
        }
    }
    &__gallery {
		overflow: hidden;
		background-color: $sony-white;
		min-height: 300px;
    }
    &__gear-container {
        background-color: $gray-200;
    }
    &__expand-icon-container {
        z-index: map-get($zindices, fixed);
        position: absolute;
        top: 20px;
		right: 5%;
		@include media-breakpoint-down(sm) {
			top: 5vw;
			right: 5vw;
        }
        cursor: pointer;
    }
	&__edit-profile-btn {
		z-index: map-get($zindices, fixed);
        position: absolute;
        top: 20px;
        left: 5%;
        cursor: pointer;
	}
    &__svg {
        height: 24px;
        width: 24px;
    }
    &__more-members-container {
        background-color: $black;
    }
    &__user-footer-container {
        // height: 75vh;
    }
}
</style>
